import { useEffect } from "react";

import { ShieldAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

import { PageLoader } from "@/components/shared";

import { PAGE_PATHS, isStaffRole } from "@/constants";
import { hardLogout } from "@/lib/session";
import { useAuthStore } from "@/store";
import { isTokenLive } from "@/store/use-auth-store";
import type { ChildProps } from "@/types";

/**
 * Ikki bosqichli qorovul: avval TIRIK sessiya bormi, keyin roli shu panelga
 * kiradimi.
 *
 * MUHIM: bu faqat INTERFEYS qorovuli. Haqiqiy cheklov backendda, har bir
 * endpointdagi `@Roles` / `@AdminOnly` da — brauzerdagi tekshiruvni istalgan
 * odam DevTools bilan chetlab o'tadi.
 *
 * "Tirik" degani token bor va muddati o'tmagan. Faqat `auth === true` ni
 * tekshirish yetmaydi: qayta yuklashdan keyin access token XOTIRADA
 * saqlanmaydi va refresh token orqali qaytadan olinadi — o'sha bir necha yuz
 * millisekundda guard odamni login'ga uloqtirib yuborardi. Shuning uchun
 * `rehydrating` alohida holat.
 */
export function AuthGuard({ children }: ChildProps) {
  const auth = useAuthStore(state => state.auth);
  const rehydrating = useAuthStore(state => state.rehydrating);
  const decodedToken = useAuthStore(state => state.decodedToken);
  const location = useLocation();

  const live = auth && isTokenLive(decodedToken);
  const staff = isStaffRole(decodedToken?.role);
  // Sessiya ochiq, lekin access token eskirgan (yoki hali yo'q). Bu —
  // login'ga uloqtirish sababi EMAS: refresh cookie tirik bo'lishi mumkin.
  const needsRefresh = auth && !live;

  // Xodim bo'lmagan odam kirib qolgan bo'lsa (masalan roli keyin
  // pasaytirilgan), sessiya shu yerda yopiladi — ekranda emas, holatda.
  useEffect(() => {
    if (live && !staff) hardLogout();
  }, [live, staff]);

  // Yangilash yiqilsa do'kon `auth` ni o'chiradi va guard login'ga yuboradi.
  useEffect(() => {
    if (!rehydrating && needsRefresh) void useAuthStore.getState().refreshSession();
  }, [rehydrating, needsRefresh]);

  if (rehydrating || needsRefresh) return <PageLoader />;

  if (!live) {
    // Chuqur havola saqlanadi: kirgandan keyin odam bosgan sahifaga qaytadi,
    // boshqaruv paneliga emas.
    return (
      <Navigate
        to={PAGE_PATHS.LOGIN}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!staff) return <StaffOnlyNotice />;

  return <>{children}</>;
}

/** Xodim bo'lmagan foydalanuvchiga ko'rsatiladigan xabar. */
function StaffOnlyNotice() {
  const { t } = useTranslation();

  useEffect(() => {
    toast.error(t("AUTH.STAFF_ONLY"));
  }, [t]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 text-center">
      <span className="bg-destructive/10 text-destructive mb-5 grid size-14 place-items-center rounded-full">
        <ShieldAlertIcon className="size-6" />
      </span>
      <h1 className="text-xl font-semibold">{t("AUTH.STAFF_ONLY")}</h1>
      <p className="text-muted-foreground mt-2 max-w-md text-sm">{t("AUTH.STAFF_ONLY_HINT")}</p>
    </div>
  );
}
